//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// autopilot-suggestion-lessen-constraint imports
import { IState, IProps, IContainer } from "./AutopilotSuggestionLessenConstraint.d";
import * as s from "./AutopilotSuggestionLessenConstraint.styles";
// other components
import { H2 } from "@ui";
// util
import { createEventParentRelayIdFromOrgRelayId } from "@util/event.util";
import { generateAutopilotTags, EFlex } from "@util/event-tag.util";
import { track, TrackingEvents } from "@util/analytics.util";
import { logger } from "@util/logger.util";
// state
import { IReduxState } from "@state/reducers/root.reducer";
import { snackBarOpen } from "@actions/snack-bar.actions";
import {
  addAndRemoveOrgTags,
  markDefragValueKnobsAsPresented,
  markDefragValueKnobsAsAccepted,
} from "@mutations";
// material-ui
import { Button } from "@material-ui/core";
import { AccessTime, InfoOutlined } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
class AutopilotSuggestionLessenConstraintComponent extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      enabling: false,
      enabled: false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public componentDidMount() {
    if (this.props.active) {
      this.markAsPresented();
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (nextProps.active && !this.props.active) {
      this.markAsPresented();
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private onSuccess = () => {
    this.setState({ enabling: false, enabled: true });
    this.props.snackBarOpen("Saved event preferences");
    this.markAsAccepted();
  };

  private onFailure = () => {
    this.setState({ enabling: false });
    logger.error("Failed to save event preferences in Autopilot suggestions");
    this.props.snackBarOpen("Failed to save event preferences.", "error");
  };

  private increaseAutopilotFlexibility = (_event: ISchema.IEvent) => {
    this.setState({ enabling: true });
    const { relay, org, lessenAutopilotConstraintKnob, calendarId } = this.props;

    // actually enable autopilot
    const eventParentRelayId = createEventParentRelayIdFromOrgRelayId(
      org.id,
      lessenAutopilotConstraintKnob.externalEventId,
    );
    if (!eventParentRelayId) {
      return;
    }

    // create tags for saving
    const autopilotOptions = {
      flex: EFlex.Week,
    };
    const tags = generateAutopilotTags(autopilotOptions);

    // add the org id and eventparent id to the input
    const input = {
      eventParentRelayId,
      calendarId,
      ...tags,
      orgRelayId: org.id,
    };

    // finally, mutate
    addAndRemoveOrgTags(relay.environment, input, this.onSuccess, this.onFailure);

    // callback
    this.props.onAction();
    track(TrackingEvents.AUTOPILOT_SUGGESTIONS.AUTOPILOT_SUGGESTION_LESSEN_CONSTRAINT_CLICKED);
  };

  private onMarkAsPresentedSuccess = () => {
    this.props.onMarkAsPresentedCallback(this.props.lessenAutopilotConstraintKnob.knobUrn);
  };

  private onMarkAsPresentedFailure = () => {
    logger.error("Failed to mark autopilot suggestion lessen constraint as presented");
  };

  private markAsPresented = () => {
    markDefragValueKnobsAsPresented(
      this.props.relay.environment,
      {
        knobUrns: [this.props.lessenAutopilotConstraintKnob.knobUrn],
        orgRelayId: this.props.org.id,
      },
      this.onMarkAsPresentedSuccess,
      this.onMarkAsPresentedFailure,
    );
  };

  private onMarkAsAcceptedFailure = () => {
    logger.error("Failed to mark autopilot invite suggestion as accepted");
  };

  private markAsAccepted = () => {
    markDefragValueKnobsAsAccepted(
      this.props.relay.environment,
      {
        knobUrns: [this.props.lessenAutopilotConstraintKnob.knobUrn],
        orgRelayId: this.props.org.id,
      },
      () => null,
      this.onMarkAsAcceptedFailure,
    );
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderAction = (event: ISchema.IEvent) => {
    const { classes } = this.props;

    return (
      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={() => this.increaseAutopilotFlexibility(event)}
        classes={{ root: classes.button }}
        disabled={this.state.enabling || this.state.enabled}
      >
        <div>{this.state.enabled ? `Preferences updated` : `Yes, update setting`}</div>
      </Button>
    );
  };

  private renderEventTime = () => {
    const { classes } = this.props;

    return (
      <div className={classes.eventTimeContainer}>
        <AccessTime {...s.clockIcon} />
        <div>
          <div className={classes.strikethrough}>Ok to move within the day</div>
          <div className={classes.green}>Ok to move within the week</div>
        </div>
      </div>
    );
  };

  private renderEvent = (event: ISchema.IEvent) => {
    const { classes, lessenAutopilotConstraintKnob } = this.props;
    const isOnNoMeetingDay =
      lessenAutopilotConstraintKnob.affectedGoals.findIndex(
        (g) => g === ISchema.AffectedGoals.NoMeetingDay,
      ) !== -1;

    return (
      <div className={classes.event}>
        <div className={classes.orangeBar}></div>
        <div>
          <div className={classes.title}>{event.title}</div>
          <div className={classes.date}>
            <InfoOutlined {...s.infoIcon} />
            <div>
              {isOnNoMeetingDay
                ? `This event has been scheduled on a no-meeting day`
                : `Clockwise can't find a better time within the day for this event`}
            </div>
          </div>
        </div>
      </div>
    );
  };

  public render() {
    const { classes, referencedEvents, lessenAutopilotConstraintKnob } = this.props;

    const event = referencedEvents.find(
      (e) => e.eventKey.externalEventId === lessenAutopilotConstraintKnob.externalEventId,
    );
    if (!event) {
      logger.error("cannot find referenced event for lessen autopilot constraint knob");
      return "";
    }

    return (
      <div className={classes.container}>
        <H2 style={s.styles.header}>Make the timing of this event more flexible</H2>
        {this.renderEvent(event)}
        {this.renderEventTime()}
        {this.renderAction(event)}
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(AutopilotSuggestionLessenConstraintComponent);

//////////////////
// WITH STYLES
//////////////////
const AutopilotSuggestionLessenConstraintStyled = withStyles(s.styles)(reduxContainer);

//////////////////
// RELAY
//////////////////
import { fragments } from "./AutopilotSuggestionLessenConstraint.gql";
export const AutopilotSuggestionLessenConstraint = createFragmentContainer<IContainer>(
  AutopilotSuggestionLessenConstraintStyled,
  fragments,
);
